'use client'

import { type ReactNode } from 'react'
import Link from 'next/link'
import { deliveryFrequencies, marketplaceLabels, shipmentTypeLabels, type FulfillmentApi } from '@entities/fulfillment'
import { Button } from '@ui/base/button'
import { Card, CardContent } from '@ui/base/card'
import { Checkbox } from '@ui/base/checkbox'
import { Label } from '@ui/base/label'
import { Separator } from '@ui/base/separator'
import { toggleValue } from '@utils/toggle-value'
import { typeClass } from '@utils/typography'

import { useCatalogFilters } from './catalog-filters-context'

const shipmentOptions = Object.entries(shipmentTypeLabels) as Array<[FulfillmentApi.ShipmentType, string]>
const marketplaceOptions = Object.entries(marketplaceLabels) as Array<[FulfillmentApi.Marketplace, string]>

const FilterSection = ({ title, id, children }: { title: string; id: string; children: ReactNode }) => (
  <div className="grid min-w-0 gap-2">
    <span className={typeClass.label} id={id}>
      {title}
    </span>
    {children}
  </div>
)

/**
 * Отмеченные значения складываются: фулфилмент должен подходить сразу под все.
 * Число справа — сколько карточек останется, если добавить это значение к отбору.
 */
const FilterCheckbox = ({
  id,
  label,
  count,
  checked,
  onToggle,
}: {
  id: string
  label: string
  count: number | null
  checked: boolean
  onToggle: () => void
}) => (
  <Label htmlFor={id} className="cursor-pointer justify-between">
    <span className="flex items-center gap-2">
      <Checkbox id={id} aria-labelledby={`${id}-title`} checked={checked} onCheckedChange={onToggle} />
      <span id={`${id}-title`}>{label}</span>
    </span>
    {count !== null && <span className={`${typeClass.caption} text-muted-foreground`}>{count}</span>}
  </Label>
)

export const CatalogFilters = ({
  counts,
  children,
}: {
  /** Пусто, когда список не загрузился: тогда числа рядом с фильтрами не показываем. */
  counts: FulfillmentApi.FilterCounts | null
  children: ReactNode
}) => {
  const { current, navigate } = useCatalogFilters()

  const hasFilters =
    Boolean(current.query || current.city || current.deliveryFrequency) ||
    current.shipmentTypes.length > 0 ||
    current.marketplaces.length > 0

  const toggleShipmentType = (type: FulfillmentApi.ShipmentType) =>
    navigate(({ shipmentTypes }) => ({ shipmentTypes: toggleValue(shipmentTypes, type) }))

  const toggleMarketplace = (marketplace: FulfillmentApi.Marketplace) =>
    navigate(({ marketplaces }) => ({ marketplaces: toggleValue(marketplaces, marketplace) }))

  /** Повторная отметка снимает фильтр: отдельная кнопка «любая частота» не нужна. */
  const toggleDeliveryFrequency = (frequency: FulfillmentApi.DeliveryFrequency) =>
    navigate((current) => ({
      deliveryFrequency: current.deliveryFrequency === frequency ? undefined : frequency,
    }))

  return (
    <Card>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className={typeClass.sectionTitle}>Фильтры</span>
          {hasFilters && (
            <Button variant="link" size="sm" className="h-auto p-0" render={<Link href="/fulfillment" />}>
              Сбросить
            </Button>
          )}
        </div>

        <Separator />

        <FilterSection title="Тип поставки" id="catalog-shipment-type-label">
          <div role="group" aria-labelledby="catalog-shipment-type-label" className="grid gap-2.5">
            {shipmentOptions.map(([value, label]) => (
              <FilterCheckbox
                key={value}
                id={`catalog-shipment-type-${value}`}
                label={label}
                count={counts ? (counts.shipment_types[value] ?? 0) : null}
                checked={current.shipmentTypes.includes(value)}
                onToggle={() => toggleShipmentType(value)}
              />
            ))}
          </div>
        </FilterSection>

        <Separator />

        {children}

        <Separator />

        <FilterSection title="Возит на маркетплейс" id="catalog-delivery-frequency-label">
          <div role="group" aria-labelledby="catalog-delivery-frequency-label" className="grid gap-2.5">
            {deliveryFrequencies.map(({ code, label }) => (
              <FilterCheckbox
                key={code}
                id={`catalog-delivery-frequency-${code}`}
                label={label}
                count={null}
                checked={current.deliveryFrequency === code}
                onToggle={() => toggleDeliveryFrequency(code)}
              />
            ))}
          </div>
          <span className={`${typeClass.caption} text-muted-foreground`}>
            Останутся фулфилменты, которые возят не реже выбранного.
          </span>
        </FilterSection>

        <Separator />

        <FilterSection title="Маркетплейсы" id="catalog-marketplace-label">
          <div role="group" aria-labelledby="catalog-marketplace-label" className="grid gap-2.5">
            {marketplaceOptions.map(([value, label]) => (
              <FilterCheckbox
                key={value}
                id={`catalog-marketplace-${value}`}
                label={label}
                count={counts ? (counts.marketplaces[value] ?? 0) : null}
                checked={current.marketplaces.includes(value)}
                onToggle={() => toggleMarketplace(value)}
              />
            ))}
          </div>
        </FilterSection>
      </CardContent>
    </Card>
  )
}
